import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface AuditEntry {
  id: string;
  timestamp: string;
  action: string;
  description: string;
  actor?: string;
  family?: string;
  version?: string;
}

/** Maximum entries kept in localStorage to avoid quota exceeded errors. */
const MAX_ENTRIES = 500;

interface AuditState {
  entries: AuditEntry[];
  actor: string | undefined;
  setActor: (actor: string | undefined) => void;
  addEntry: (
    action: string,
    description: string,
    family?: string,
    version?: string,
    actor?: string,
  ) => void;
}

export const useAuditStore = create<AuditState>()(
  persist(
    (set) => ({
      entries: [],
      actor: undefined,
      setActor: (actor) => set({ actor }),
      addEntry: (action, description, family, version, actor) =>
        set((s) => {
          const newEntries = [
            {
              id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              timestamp: new Date().toISOString(),
              action,
              description,
              actor: actor ?? s.actor,
              ...(family ? { family } : {}),
              ...(version ? { version } : {}),
            },
            ...s.entries,
          ];
          // Trim oldest entries to stay within the limit
          return { entries: newEntries.slice(0, MAX_ENTRIES) };
        }),
    }),
    {
      name: "shelf-audit-log",
      // Handle localStorage quota errors gracefully
      storage: {
        getItem: (name) => {
          try {
            const str = localStorage.getItem(name);
            return str ? JSON.parse(str) : null;
          } catch {
            return null;
          }
        },
        setItem: (name, value) => {
          try {
            localStorage.setItem(name, JSON.stringify(value));
          } catch {
            // localStorage quota exceeded — silently drop the write
          }
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    },
  ),
);
